import type { AreaKind } from '../../areaCatalog';
import type { UnableReason } from '../../inspection';
import type { LimitationTemplateId } from './limitationTemplates';

// NY SCOPE-SUBJECT CATALOGUE — DATA (docs/KNOWLEDGE-IMPORT-MANIFEST.md K-23).
//
// Source: docs/authority/ny/NY-SCOPE-MATRIX.md §2 (core systems) and §3 (special items),
// docs/authority/ny/NY-SPECIAL-SYSTEMS-SCOPE.md §1 … §15 (the five separate answers per item) and
// docs/authority/ny/NY-INSPECTION-LIMITATIONS.md §4 (obstruction object vs concealed component).
// Frozen plan §5.5 / §8: the two axes of the scope model.
//
//   Axis A — LegalStatus      what New York text says about the subject. Fixed by the authority,
//                             never per inspection, never derived from anything the AI produced.
//   Axis B — ScopeDisposition what THIS product does about it for THIS inspection. Derived in
//                             src/domain/scopeGate.ts from Axis A + the property facts + the signed
//                             agreement, then frozen into the inspection's scope snapshot.
//
// Hard rules carried from the authority (master §4): `NOT_REQUIRED_BY_NY_SOP` is never written as
// "prohibited" or "excluded by New York"; only `STATUTORY_EXCLUSION` subjects are outside the
// statutory home inspection; the product's own default (`OUT_OF_SCOPE_PRODUCT_POLICY`) is never
// presented as New York's ruling. The inspector never sees this vocabulary (plan §8.5).
//
// Pure DATA + pure lookups: no store, no i18n, no network.
export type LegalStatus =
  | 'MANDATORY_BY_NY_SOP'
  | 'CONDITIONAL_MANDATORY'
  | 'NOT_REQUIRED_BY_NY_SOP'
  | 'STATUTORY_EXCLUSION'
  | 'EXPLICIT_PROHIBITION'
  | 'LEGAL_REVIEW_REQUIRED';

export type ScopeDisposition =
  | 'MANDATORY_IN_SCOPE'
  | 'CONDITIONAL_SCOPE'
  | 'CONTRACT_INCLUDED'
  | 'OUT_OF_SCOPE_PRODUCT_POLICY'
  | 'CLIENT_EXCLUDED_IF_LAWFULLY_PERMITTED'
  | 'STATUTORY_EXCLUSION'
  | 'EXPLICIT_PROHIBITION'
  | 'LIMITATION'
  | 'NOT_INSTALLED_NA'
  | 'LEGAL_REVIEW_REQUIRED'
  | 'LEGAL_REVIEW_BLOCK';

export const LEGAL_STATUS_ORDER: readonly LegalStatus[] = Object.freeze([
  'MANDATORY_BY_NY_SOP',
  'CONDITIONAL_MANDATORY',
  'NOT_REQUIRED_BY_NY_SOP',
  'STATUTORY_EXCLUSION',
  'EXPLICIT_PROHIBITION',
  'LEGAL_REVIEW_REQUIRED'
]);

export const SCOPE_DISPOSITION_ORDER: readonly ScopeDisposition[] = Object.freeze([
  'MANDATORY_IN_SCOPE',
  'CONDITIONAL_SCOPE',
  'CONTRACT_INCLUDED',
  'OUT_OF_SCOPE_PRODUCT_POLICY',
  'CLIENT_EXCLUDED_IF_LAWFULLY_PERMITTED',
  'STATUTORY_EXCLUSION',
  'EXPLICIT_PROHIBITION',
  'LIMITATION',
  'NOT_INSTALLED_NA',
  'LEGAL_REVIEW_REQUIRED',
  'LEGAL_REVIEW_BLOCK'
]);

export function isLegalStatus(value: unknown): value is LegalStatus {
  return typeof value === 'string' && (LEGAL_STATUS_ORDER as readonly string[]).includes(value);
}

export function isScopeDisposition(value: unknown): value is ScopeDisposition {
  return typeof value === 'string' && (SCOPE_DISPOSITION_ORDER as readonly string[]).includes(value);
}

export type ScopeSubjectId =
  // in scope — the systems and components the Standards of Practice require
  | 'site'
  | 'structure'
  | 'foundation'
  | 'exterior-cladding'
  | 'windows-doors'
  | 'stairs-railings'
  | 'attached-structures'
  | 'roof'
  | 'roof-drainage'
  | 'chimney'
  | 'fireplace'
  | 'attic'
  | 'insulation-ventilation'
  | 'basement'
  | 'crawlspace'
  | 'sump-pump'
  | 'plumbing'
  | 'water-heater'
  | 'fuel-storage'
  | 'electrical'
  | 'smoke-co-detectors'
  | 'heating'
  | 'cooling'
  | 'interior'
  | 'garage'
  | 'inspector-defined-area'
  // not required by the New York Standards of Practice (never "excluded by New York")
  | 'solar'
  | 'generator'
  | 'pool-spa'
  | 'recreational-facilities'
  | 'irrigation'
  | 'well'
  | 'septic'
  | 'sewer-lateral'
  | 'water-treatment'
  | 'water-quality'
  | 'water-temperature'
  | 'household-appliances'
  | 'window-wall-ac'
  | 'security-low-voltage'
  | 'central-vacuum'
  | 'outbuildings'
  | 'fences'
  | 'elevators-lifts'
  | 'underground-tank'
  | 'condo-common-elements'
  | 'mold'
  | 'asbestos-lead'
  // outside the statutory home inspection by definition (§444-b(4))
  | 'radon'
  | 'pest-wdi'
  // obstruction objects — never a task of their own; they turn an in-scope component into a limitation
  | 'stored-items'
  | 'finishes-coverings'
  | 'vegetation-obstruction'
  | 'snow-ice';

// What a CONDITIONAL_MANDATORY subject needs before it is in scope for this inspection.
//   propertyFact  a fact the inspector recorded at setup (never inferred, never from AI)
//   areaKind      an enabled area of this kind exists (the inspector's own layout)
//   rowRecorded   a checklist row of this subject exists and is not marked not-present
export type SubjectTrigger =
  | { kind: 'propertyFact'; fact: 'basementType' | 'heatingFuel' | 'propertyType'; oneOf: readonly string[] }
  | { kind: 'areaKind'; areaKind: AreaKind }
  | { kind: 'rowRecorded' };

// How an `otherIssue` result about a subject that is NOT in scope is treated (AI policy §3 step 3):
//   observationAllowed  visible evidence of this subject is an observation of the building's own
//                       condition and is always allowed as an observation (never a determination,
//                       never a task) — pest / WDI evidence, mold-like staining, septic surface
//                       indications, suspect materials;
//   hazardOrThreatOnly  only a visible hazard on an in-scope system, or deterioration that threatens
//                       the residential building, survives; anything else is scope noise and becomes
//                       the OD-13 scope reminder.
export type OtherIssuePolicy = 'observationAllowed' | 'hazardOrThreatOnly';

export interface ScopeSubject {
  id: ScopeSubjectId;
  legalStatus: LegalStatus;
  ruleIds: readonly string[];
  // Checklist template rows that ARE this subject (src/domain/checklistTemplates.ts).
  rows: readonly string[];
  // Rows that exist beyond the New York minimum and may only ever carry an observation
  // (NY-CHECKLIST-COMPLIANCE-GAP.md §3). The gate never lets one be upgraded to an evaluation.
  observationOnlyRows: boolean;
  triggers: readonly SubjectTrigger[];
  otherIssuePolicy: OtherIssuePolicy;
  // Stored items, finishes, vegetation and snow are NOT scope subjects of their own
  // (NY-INSPECTION-LIMITATIONS.md §4: "the obstruction object … not a scope subject"). They take
  // part in the obstruction pass only; where nothing is concealed, the result keeps resolving to the
  // component it is really about — "stored items against the water heater" is a water-heater
  // observation, not an out-of-scope subject.
  obstructionOnly: boolean;
  // An obstruction object: seeing it never creates work on itself; it converts to a limitation on
  // the in-scope component it conceals (NY-INSPECTION-LIMITATIONS.md §4; PP-13).
  obstruction: null | {
    // The in-scope subjects whose observation it can limit ([] = whatever row the result matched).
    limitationOf: readonly ScopeSubjectId[];
    // The row the limitation lands on when the result did not match one.
    targetRow: string | null;
    reasonCode: UnableReason;
    // The frozen limitation wording the inspector may accept with it (null = the generic template).
    templateId: LimitationTemplateId | null;
  };
  // The property fact this subject should make the inspector answer instead of inspecting it
  // (NY-SPECIAL-SYSTEMS-SCOPE.md §3 / §4: "water source = private?"), or null.
  descriptorPrompt: 'waterSupplySource' | 'wasteDisposalSource' | null;
  // Deterministic keyword / synonym table (AI policy §3 step 1), versioned with this authority.
  // EN entries match whole words, case-insensitively; ZH entries match as substrings.
  keywords: { en: readonly string[]; zh: readonly string[] };
  // Resolution weight added to this subject's longest keyword match. It exists for ONE class of
  // subject: the ones whose visible evidence is always an observation and never suppressed
  // (`observationAllowed`). "Mud tubes on the foundation wall" is about the mud tubes, not about the
  // wall — without the bonus the location would outrank the evidence and the referral wording of
  // NY-SPECIAL-SYSTEMS-SCOPE.md §8 would be lost. It can never turn an in-scope matter into a
  // rejected one, because those subjects' results are always allowed as observations.
  keywordBonus: number;
}

interface SubjectShape {
  rows?: readonly string[];
  observationOnlyRows?: boolean;
  triggers?: readonly SubjectTrigger[];
  otherIssuePolicy?: OtherIssuePolicy;
  obstructionOnly?: boolean;
  obstruction?: ScopeSubject['obstruction'];
  descriptorPrompt?: ScopeSubject['descriptorPrompt'];
  en?: readonly string[];
  zh?: readonly string[];
}

// The evidence of an "observation allowed" subject outranks the place it was seen (see keywordBonus).
const OBSERVATION_EVIDENCE_BONUS = 8;

function subject(id: ScopeSubjectId, legalStatus: LegalStatus, ruleIds: readonly string[], shape: SubjectShape = {}): ScopeSubject {
  return Object.freeze({
    id,
    legalStatus,
    ruleIds: Object.freeze([...ruleIds]),
    rows: Object.freeze([...(shape.rows ?? [])]),
    observationOnlyRows: shape.observationOnlyRows ?? false,
    triggers: Object.freeze([...(shape.triggers ?? [])]),
    otherIssuePolicy: shape.otherIssuePolicy ?? 'hazardOrThreatOnly',
    obstructionOnly: shape.obstructionOnly ?? false,
    obstruction: shape.obstruction ?? null,
    descriptorPrompt: shape.descriptorPrompt ?? null,
    keywords: Object.freeze({ en: Object.freeze([...(shape.en ?? [])]), zh: Object.freeze([...(shape.zh ?? [])]) }),
    keywordBonus: (shape.otherIssuePolicy ?? 'hazardOrThreatOnly') === 'observationAllowed' ? OBSERVATION_EVIDENCE_BONUS : 0
  });
}

// Order matters for resolution: the catalogue is scanned in this order and the FIRST subject whose
// keyword table matches wins, so the narrow special systems are listed before the broad ones.
export const NY_SCOPE_SUBJECTS: readonly ScopeSubject[] = Object.freeze([
  // ---------- obstruction objects (checked first, and only together with concealment wording) ----------
  subject('solar', 'NOT_REQUIRED_BY_NY_SOP', ['NY-EXC-05', 'NY-EXC-04', 'NY-EXC-06', 'NY-SOP-08', 'PP-18'], {
    obstruction: { limitationOf: ['roof'], targetRow: 'roof-covering', reasonCode: 'obstructed', templateId: 'solarArrayRoof' },
    en: ['solar', 'solar panel', 'solar panels', 'solar array', 'photovoltaic', 'pv array', 'pv panels', 'inverter', 'micro-inverter', 'solar water heating'],
    zh: ['太阳能', '光伏', '逆变器', '太阳能板', '太阳能电池板']
  }),
  subject('snow-ice', 'NOT_REQUIRED_BY_NY_SOP', ['NY-LIM-02', 'NY-SAF-03'], {
    obstructionOnly: true,
    obstruction: { limitationOf: ['roof', 'site'], targetRow: null, reasonCode: 'weather', templateId: null },
    en: ['snow', 'snow cover', 'snow-covered', 'covered in ice', 'ice cover', 'frost'],
    zh: ['积雪', '冰雪', '结冰', '覆雪']
  }),
  subject('stored-items', 'NOT_REQUIRED_BY_NY_SOP', ['NY-LIM-01', 'NY-LIM-02'], {
    obstructionOnly: true,
    obstruction: { limitationOf: [], targetRow: null, reasonCode: 'obstructed', templateId: null },
    en: ['stored items', 'stored belongings', 'storage', 'boxes', 'furniture', 'personal property', 'clutter'],
    zh: ['存放物', '储物', '杂物', '纸箱', '家具', '堆放']
  }),
  subject('finishes-coverings', 'NOT_REQUIRED_BY_NY_SOP', ['NY-LIM-01', 'NY-DES-02'], {
    obstructionOnly: true,
    obstruction: { limitationOf: [], targetRow: null, reasonCode: 'obstructed', templateId: null },
    en: ['drywall', 'finished ceiling', 'finished wall', 'paneling', 'panelling', 'wall covering', 'floor covering', 'carpet', 'insulation over'],
    zh: ['饰面', '装修覆盖', '石膏板', '吊顶', '地毯', '地面覆盖', '保温层覆盖']
  }),
  subject('vegetation-obstruction', 'NOT_REQUIRED_BY_NY_SOP', ['NY-LIM-02', 'NY-EXC-01'], {
    obstructionOnly: true,
    obstruction: { limitationOf: [], targetRow: null, reasonCode: 'obstructed', templateId: null },
    en: ['ivy', 'vines', 'overgrown shrubs', 'overgrown vegetation', 'dense shrubs'],
    zh: ['藤蔓', '爬藤', '灌木遮挡', '植被遮挡', '杂草']
  }),

  // ---------- outside the statutory home inspection (§444-b(4)) ----------
  subject('radon', 'STATUTORY_EXCLUSION', ['NY-ANC-01', 'NY-LIC-10'], {
    en: ['radon', 'radon mitigation', 'radon fan', 'radon test'],
    zh: ['氡', '氡气', '除氡']
  }),
  subject('pest-wdi', 'STATUTORY_EXCLUSION', ['NY-ANC-01', 'NY-ANC-02', 'NY-ENV-01'], {
    rows: ['ext-wdi', 'safe-wdi', 'bsmt-pests-mold', 'bsmt-pests', 'attic-pests'],
    observationOnlyRows: true,
    otherIssuePolicy: 'observationAllowed',
    en: ['termite', 'termites', 'wood-destroying', 'wood destroying insect', 'wdi', 'carpenter ant', 'carpenter ants', 'powderpost beetle', 'mud tube', 'mud tubes', 'frass', 'pest', 'pests', 'rodent', 'rodents', 'infestation'],
    zh: ['白蚁', '蛀木', '木材害虫', '虫害', '泥管', '木粉', '蚁害', '鼠害', '啮齿']
  }),

  // ---------- not required by the Standards of Practice ----------
  subject('generator', 'NOT_REQUIRED_BY_NY_SOP', ['NY-EXC-05', 'PP-18'], {
    en: ['generator', 'standby generator', 'emergency generator', 'transfer switch'],
    zh: ['发电机', '备用发电', '转换开关']
  }),
  subject('pool-spa', 'NOT_REQUIRED_BY_NY_SOP', ['NY-EXC-02', 'NY-EXC-04', 'NY-EXC-10'], {
    en: ['swimming pool', 'pool pump', 'pool equipment', 'pool deck', 'pool heater', 'spa', 'hot tub', 'jetted tub', 'sauna'],
    zh: ['泳池', '游泳池', '温泉池', '按摩浴缸', '桑拿', '池水设备']
  }),
  subject('recreational-facilities', 'NOT_REQUIRED_BY_NY_SOP', ['NY-EXC-02', 'NY-EXC-08', 'NY-EXC-10'], {
    en: ['playground', 'play set', 'tennis court', 'basketball court', 'trampoline'],
    zh: ['游乐设施', '球场', '秋千', '蹦床']
  }),
  subject('irrigation', 'NOT_REQUIRED_BY_NY_SOP', ['NY-EXC-10', 'NY-EXC-05'], {
    en: ['irrigation', 'sprinkler system', 'lawn sprinkler', 'drip line'],
    zh: ['灌溉', '喷淋系统', '草坪喷灌']
  }),
  subject('well', 'NOT_REQUIRED_BY_NY_SOP', ['NY-EXC-04', 'NY-ENV-03', 'NY-RPT-05'], {
    // Round 5 (CK-09): the conditional surface-indication row an inspector gets when the water
    // supply is answered "private" — visible well head, cover, pressure tank and ground signs only,
    // never an assessment of the well system, its yield or the water quality.
    rows: ['ext-well'],
    observationOnlyRows: true,
    otherIssuePolicy: 'observationAllowed',
    descriptorPrompt: 'waterSupplySource',
    en: ['private well', 'water well', 'drilled well', 'dug well', 'well pump', 'well pressure tank', 'pressure tank', 'well head', 'wellhead', 'well casing', 'jet pump', 'submersible pump'],
    zh: ['水井', '井泵', '压力罐', '深井', '私有供水']
  }),
  subject('septic', 'NOT_REQUIRED_BY_NY_SOP', ['NY-EXC-04', 'NY-RPT-05'], {
    rows: ['ext-septic'],
    observationOnlyRows: true,
    otherIssuePolicy: 'observationAllowed',
    descriptorPrompt: 'wasteDisposalSource',
    en: ['septic', 'septic tank', 'cesspool', 'drain field', 'leach field', 'on-site sewage', 'aerobic treatment'],
    zh: ['化粪池', '污水池', '渗滤场', '现场排污', '沼气池']
  }),
  subject('sewer-lateral', 'NOT_REQUIRED_BY_NY_SOP', ['NY-EXC-04', 'NY-EXC-10'], {
    en: ['sewer lateral', 'sewer line', 'main sewer', 'sewer camera', 'underground piping'],
    zh: ['排污干管', '市政排污管', '下水主管', '管道内窥']
  }),
  subject('water-treatment', 'NOT_REQUIRED_BY_NY_SOP', ['NY-EXC-04'], {
    en: ['water softener', 'water treatment', 'water filter', 'uv sterilizer', 'reverse osmosis'],
    zh: ['软水机', '净水', '滤水', '水处理']
  }),
  subject('water-quality', 'NOT_REQUIRED_BY_NY_SOP', ['NY-ENV-03'], {
    en: ['water quality', 'potability', 'water test', 'bacteria test'],
    zh: ['水质', '可饮用', '水质检测']
  }),
  subject('water-temperature', 'NOT_REQUIRED_BY_NY_SOP', ['NY-EXC-04'], {
    rows: ['wh-temperature'],
    observationOnlyRows: true,
    en: ['water temperature', 'scald', 'scalding', 'thermostatic mixing valve'],
    zh: ['水温', '烫伤', '出水温度']
  }),
  subject('household-appliances', 'NOT_REQUIRED_BY_NY_SOP', ['NY-EXC-08', 'NY-EXC-10', 'PP-18'], {
    rows: ['kit-appliances'],
    observationOnlyRows: true,
    en: ['kitchen appliance', 'kitchen appliances', 'laundry appliance', 'household appliance', 'household appliances', 'dishwasher', 'oven', 'cooktop', 'kitchen range', 'gas range', 'electric range', 'refrigerator', 'freezer', 'ice maker', 'microwave', 'washing machine', 'clothes dryer', 'garbage disposal', 'anti-tip bracket'],
    zh: ['电器', '家电', '洗碗机', '灶具', '烤箱', '洗衣机', '烘干机', '厨余粉碎']
  }),
  subject('window-wall-ac', 'NOT_REQUIRED_BY_NY_SOP', ['NY-EXC-07'], {
    en: ['window air conditioner', 'window ac', 'wall air conditioner', 'through-the-wall unit', 'portable air conditioner'],
    zh: ['窗式空调', '壁挂窗机', '移动空调']
  }),
  subject('security-low-voltage', 'NOT_REQUIRED_BY_NY_SOP', ['NY-EXC-02', 'NY-EXC-05', 'NY-EXC-10'], {
    en: ['security system', 'alarm system', 'burglar alarm', 'camera system', 'doorbell', 'intercom', 'smart home', 'thermostat app', 'low-voltage wiring', 'landscape lighting'],
    zh: ['安防系统', '报警系统', '监控', '门铃', '对讲', '智能家居', '低压线']
  }),
  subject('central-vacuum', 'NOT_REQUIRED_BY_NY_SOP', ['NY-EXC-08', 'NY-EXC-10'], {
    en: ['central vacuum'],
    zh: ['中央吸尘']
  }),
  subject('outbuildings', 'NOT_REQUIRED_BY_NY_SOP', ['NY-EXC-02', 'NY-EXC-10'], {
    en: ['storage shed', 'garden shed', 'detached shed', 'barn', 'pool house', 'guest cottage', 'outbuilding', 'out-building', 'detached workshop'],
    zh: ['棚屋', '谷仓', '泳池房', '附属建筑', '独立工棚']
  }),
  subject('fences', 'NOT_REQUIRED_BY_NY_SOP', ['NY-EXC-01', 'NY-EXC-02'], {
    en: ['fence', 'fencing', 'privacy wall', 'gate hardware'],
    zh: ['围栏', '栅栏', '隐私墙']
  }),
  subject('elevators-lifts', 'NOT_REQUIRED_BY_NY_SOP', ['NY-EXC-08'], {
    en: ['elevator', 'chair lift', 'stair lift', 'dumbwaiter', 'wheelchair lift'],
    zh: ['电梯', '升降椅', '送餐梯', '升降平台']
  }),
  subject('underground-tank', 'NOT_REQUIRED_BY_NY_SOP', ['NY-EXC-10'], {
    en: ['underground tank', 'buried tank', 'buried piping', 'abandoned tank', 'ust'],
    zh: ['埋地储罐', '地埋油罐', '埋地管线']
  }),
  subject('condo-common-elements', 'NOT_REQUIRED_BY_NY_SOP', ['NY-EXC-10'], {
    en: ['common element', 'common elements', 'common area roof', 'reserve account', 'hoa'],
    zh: ['共有部分', '公共区域', '公共设施', '业委会']
  }),
  subject('mold', 'NOT_REQUIRED_BY_NY_SOP', ['NY-ENV-01', 'NY-ENV-02'], {
    // 2026-09-13 (ratification §7): the mold observation row split out of bsmt-pests-mold.
    rows: ['bsmt-mold'],
    otherIssuePolicy: 'observationAllowed',
    en: ['mold', 'mould', 'microbial growth', 'mildew'],
    zh: ['霉', '霉菌', '发霉', '微生物生长']
  }),
  subject('asbestos-lead', 'NOT_REQUIRED_BY_NY_SOP', ['NY-ENV-01'], {
    otherIssuePolicy: 'observationAllowed',
    en: ['asbestos', 'lead paint', 'lead-based paint', 'vermiculite', 'urea formaldehyde'],
    zh: ['石棉', '铅漆', '含铅油漆', '蛭石']
  }),

  // ---------- in scope: the Standards-of-Practice systems and components ----------
  subject('roof', 'MANDATORY_BY_NY_SOP', ['NY-SOP-08', 'NY-RPT-04'], {
    rows: ['roof-covering', 'roof-method', 'roof-flashing', 'roof-valleys-edges', 'roof-skylight'],
    en: ['roof', 'roofing', 'shingle', 'shingles', 'roof covering', 'flashing', 'skylight', 'valley', 'drip edge', 'roof penetration', 'ice dam', 'ice dams'],
    zh: ['屋面', '屋顶', '瓦', '泛水', '天窗', '谷线', '滴水板', '屋面穿透', '冰坝']
  }),
  subject('roof-drainage', 'MANDATORY_BY_NY_SOP', ['NY-SOP-08'], {
    rows: ['ext-gutters', 'roof-gutter-attachment'],
    en: ['gutter', 'gutters', 'downspout', 'downspouts', 'leader', 'splash block', 'roof drainage'],
    zh: ['天沟', '落水管', '雨水管', '溅水板', '排水槽']
  }),
  subject('chimney', 'CONDITIONAL_MANDATORY', ['NY-SOP-15', 'NY-SOP-08'], {
    rows: ['roof-chimney'],
    triggers: [{ kind: 'rowRecorded' }],
    en: ['chimney', 'chimneys', 'chimney crown', 'chimney cap', 'flue liner'],
    zh: ['烟囱', '烟囱帽', '烟道内衬']
  }),
  subject('fireplace', 'CONDITIONAL_MANDATORY', ['NY-SOP-15'], {
    rows: ['int-fireplace'],
    triggers: [{ kind: 'rowRecorded' }],
    en: ['fireplace', 'wood stove', 'wood-burning stove', 'solid fuel appliance', 'hearth', 'damper'],
    zh: ['壁炉', '柴炉', '固体燃料', '炉膛', '风门']
  }),
  subject('attic', 'MANDATORY_BY_NY_SOP', ['NY-SOP-16'], {
    rows: ['attic-access', 'attic-sheathing'],
    en: ['attic', 'attic hatch', 'attic access', 'roof sheathing', 'scuttle'],
    zh: ['阁楼', '屋面板', '检修口']
  }),
  subject('insulation-ventilation', 'MANDATORY_BY_NY_SOP', ['NY-SOP-14'], {
    rows: ['attic-insulation', 'attic-ventilation', 'attic-exhaust-termination', 'roof-ventilation', 'bsmt-foundation-insulation', 'bath-exhaust', 'kit-range-vent', 'kit-exhaust-ducting'],
    en: ['insulation', 'ventilation', 'soffit vent', 'ridge vent', 'gable vent', 'exhaust fan', 'bath fan', 'range hood', 'vent duct', 'dryer vent'],
    zh: ['保温', '隔热', '通风', '檐口通风', '屋脊通风', '排风扇', '抽油烟', '排风管']
  }),
  subject('structure', 'MANDATORY_BY_NY_SOP', ['NY-SOP-06'], {
    rows: ['str-main-beam', 'str-post-base', 'str-joists', 'str-connections', 'str-bearing-walls', 'str-exterior-signs', 'str-limitations', 'bsmt-structure-alignment', 'attic-structure'],
    en: ['framing', 'joist', 'joists', 'rafter', 'rafters', 'truss', 'trusses', 'beam', 'girder', 'column', 'post', 'bearing wall', 'load path', 'structural member', 'sill plate'],
    zh: ['框架', '托梁', '椽', '桁架', '主梁', '支撑柱', '承重墙', '结构件', '搁栅']
  }),
  subject('foundation', 'MANDATORY_BY_NY_SOP', ['NY-SOP-06', 'NY-SOP-05'], {
    rows: ['ext-foundation-visible', 'bsmt-foundation-walls', 'bsmt-moisture', 'bsmt-outside-review'],
    en: ['foundation', 'foundation wall', 'footing', 'slab', 'crack in the foundation', 'efflorescence', 'water penetration', 'seepage'],
    zh: ['地基', '基础墙', '地基墙', '返潮', '白霜', '渗水痕迹']
  }),
  subject('site', 'MANDATORY_BY_NY_SOP', ['NY-SOP-05'], {
    rows: ['ext-grading', 'ext-driveway', 'ext-neighbor-influence'],
    en: ['grading', 'grade', 'drainage away', 'negative slope', 'walkway', 'driveway', 'patio', 'retaining wall', 'tree', 'trees', 'vegetation'],
    zh: ['坡度', '排水坡', '倒坡', '步道', '车道', '露台', '挡土墙', '树木', '植被']
  }),
  subject('exterior-cladding', 'MANDATORY_BY_NY_SOP', ['NY-SOP-07'], {
    rows: ['ext-wall-covering'],
    en: ['siding', 'cladding', 'stucco', 'brick veneer', 'trim board', 'fascia', 'soffit', 'eaves'],
    zh: ['外墙', '墙板', '饰面层', '灰泥', '砖面', '檐板', '檐底', '封檐']
  }),
  subject('windows-doors', 'MANDATORY_BY_NY_SOP', ['NY-SOP-07', 'NY-SOP-13'], {
    rows: ['ext-windows-doors', 'int-windows', 'int-doors', 'bed-windows-doors', 'bed-egress', 'safe-egress'],
    en: ['window', 'windows', 'door', 'doors', 'egress window', 'lintel', 'weatherstripping', 'sash'],
    zh: ['窗', '门', '逃生窗', '过梁', '密封条']
  }),
  subject('stairs-railings', 'MANDATORY_BY_NY_SOP', ['NY-SOP-07', 'NY-SOP-13'], {
    rows: ['ext-entry-steps', 'int-stairs', 'bsmt-stairs', 'safe-handrails', 'safe-trip-fall'],
    en: ['stair', 'stairs', 'stairway', 'step', 'steps', 'handrail', 'guardrail', 'guard rail', 'railing', 'baluster', 'trip hazard'],
    zh: ['楼梯', '台阶', '踏步', '扶手', '护栏', '栏杆', '绊倒']
  }),
  subject('attached-structures', 'CONDITIONAL_MANDATORY', ['NY-SOP-07'], {
    rows: ['ext-deck-patio'],
    triggers: [{ kind: 'rowRecorded' }],
    en: ['deck', 'balcony', 'porch', 'stoop', 'ledger board', 'deck post'],
    zh: ['露台', '平台', '阳台', '门廊', '门阶', '托梁挂件']
  }),
  subject('basement', 'MANDATORY_BY_NY_SOP', ['NY-SOP-06', 'NY-SOP-13', 'NY-SOP-14'], {
    rows: ['bsmt-access-finish'],
    en: ['basement', 'cellar', 'lower level'],
    zh: ['地下室', '地下层']
  }),
  subject('crawlspace', 'CONDITIONAL_MANDATORY', ['NY-SOP-06', 'NY-SOP-14'], {
    rows: ['bsmt-crawlspace'],
    triggers: [{ kind: 'propertyFact', fact: 'basementType', oneOf: ['crawlspace'] }, { kind: 'rowRecorded' }],
    en: ['crawl space', 'crawlspace', 'vapor barrier', 'vapour barrier'],
    zh: ['爬行空间', '架空层', '防潮膜']
  }),
  subject('sump-pump', 'CONDITIONAL_MANDATORY', ['NY-SOP-09'], {
    rows: ['bsmt-sump'],
    triggers: [{ kind: 'rowRecorded' }],
    en: ['sump', 'sump pump', 'sump pit', 'sewage ejector', 'ejector pump'],
    zh: ['污水泵', '排水泵', '集水坑', '排污泵']
  }),
  subject('plumbing', 'MANDATORY_BY_NY_SOP', ['NY-SOP-09', 'NY-RPT-05'], {
    rows: ['plb-main-shutoff', 'plb-supply-piping', 'plb-drain-piping', 'plb-water-pressure', 'plb-fixtures-sample', 'plb-water-meter', 'plb-sewer-cleanout', 'plb-cross-connection', 'kit-sink', 'bath-tub-shower', 'bath-toilet', 'bath-sink', 'bath-water-flow', 'ext-hose-bibb'],
    en: ['plumbing', 'supply piping', 'drain', 'waste piping', 'vent piping', 'faucet', 'fixture', 'toilet', 'sink', 'tub', 'shower', 'shut-off valve', 'hose bibb', 'water meter', 'cleanout', 'p-trap', 'copper pipe', 'pex', 'galvanized pipe', 'cast iron pipe'],
    zh: ['给水', '排水', '通气管', '龙头', '卫生器具', '马桶', '水槽', '浴缸', '淋浴', '总阀', '止水阀', '水表', '清理口', '存水弯']
  }),
  subject('water-heater', 'MANDATORY_BY_NY_SOP', ['NY-SOP-09'], {
    rows: ['wh-overview', 'wh-data-plate', 'wh-tpr', 'wh-venting', 'wh-combustion-air', 'wh-connections', 'wh-base', 'safe-tpr'],
    en: ['water heater', 'hot water tank', 'tankless water heater', 'tpr valve', 't&p valve', 'relief valve discharge', 'expansion tank'],
    zh: ['热水器', '热水箱', '即热式', '泄压阀', '膨胀罐', '排放管']
  }),
  subject('fuel-storage', 'CONDITIONAL_MANDATORY', ['NY-SOP-09'], {
    rows: ['plb-fuel-shutoff', 'hvac-oil-tank', 'kit-gas'],
    triggers: [{ kind: 'propertyFact', fact: 'heatingFuel', oneOf: ['gas', 'oil', 'propane'] }, { kind: 'rowRecorded' }],
    en: ['fuel tank', 'oil tank', 'propane tank', 'lp tank', 'gas meter', 'gas piping', 'gas appliance', 'gas appliances', 'gas line', 'fuel line', 'main fuel shut-off', 'gas shut-off', 'regulator'],
    zh: ['油罐', '燃油箱', '丙烷罐', '燃气表', '燃气管', '燃料管线', '总燃料关断', '燃气阀']
  }),
  subject('electrical', 'MANDATORY_BY_NY_SOP', ['NY-SOP-10', 'NY-RPT-06'], {
    rows: ['elec-service-entrance', 'elec-panel-overview', 'elec-panel-interior', 'elec-panel-label', 'elec-grounding', 'elec-wiring-type', 'elec-gfci-afci', 'elec-outlets-sample', 'elec-fixtures', 'ext-gfci', 'garage-gfci', 'bsmt-gfci', 'kit-gfci', 'bath-gfci', 'int-outlets', 'bed-outlets', 'attic-wiring', 'safe-electrical-hazards'],
    en: ['electrical', 'service entrance', 'service drop', 'panel', 'sub-panel', 'breaker', 'fuse', 'branch circuit', 'conductor', 'wiring', 'junction box', 'receptacle', 'outlet', 'switch', 'gfci', 'afci', 'grounding', 'bonding', 'knob and tube', 'aluminum wiring', 'light fixture'],
    zh: ['电气', '进户线', '配电箱', '分箱', '断路器', '保险丝', '支路', '导线', '布线', '接线盒', '插座', '开关', '漏电保护', '接地', '搭接', '瓷管布线', '铝线', '灯具']
  }),
  subject('smoke-co-detectors', 'MANDATORY_BY_NY_SOP', ['NY-RPT-06', 'NY-SOP-03'], {
    rows: ['int-alarms', 'bed-alarm', 'bsmt-alarm', 'safe-alarms-system'],
    en: ['smoke alarm', 'smoke detector', 'carbon monoxide alarm', 'carbon monoxide detector', 'co alarm', 'co detector'],
    zh: ['烟感', '烟雾报警', '一氧化碳报警', '一氧化碳探测']
  }),
  subject('heating', 'MANDATORY_BY_NY_SOP', ['NY-SOP-11', 'NY-RPT-15'], {
    rows: ['hvac-heating-unit', 'hvac-operation', 'hvac-venting', 'hvac-filter', 'hvac-clearance', 'hvac-condensate', 'hvac-distribution', 'hvac-boiler-relief', 'hvac-supplementary-heat'],
    en: ['furnace', 'boiler', 'heat pump', 'heating system', 'heat exchanger', 'flue pipe', 'baseboard heater', 'radiator', 'ductwork', 'supply register', 'thermostat', 'condensate'],
    zh: ['炉', '锅炉', '热泵', '供暖', '烟道', '踢脚线电暖', '暖气片', '风管', '出风口', '恒温器', '冷凝水']
  }),
  subject('cooling', 'CONDITIONAL_MANDATORY', ['NY-SOP-12'], {
    rows: ['hvac-ac-unit', 'hvac-refrigerant'],
    triggers: [{ kind: 'rowRecorded' }],
    en: ['air conditioning', 'air conditioner', 'condenser', 'condensing unit', 'evaporator coil', 'refrigerant line', 'mini-split'],
    zh: ['空调', '室外机', '冷凝器', '蒸发器', '冷媒管', '分体机']
  }),
  subject('interior', 'MANDATORY_BY_NY_SOP', ['NY-SOP-13'], {
    rows: ['int-limitations', 'int-surfaces', 'kit-surfaces', 'kit-cabinets-counters', 'bath-surfaces', 'bed-surfaces', 'bed-closet'],
    en: ['wall', 'walls', 'ceiling', 'ceilings', 'floor', 'floors', 'flooring', 'water stain', 'cabinet', 'countertop', 'closet'],
    zh: ['墙面', '天花', '地面', '地板', '水渍', '橱柜', '台面', '壁橱']
  }),
  subject('garage', 'CONDITIONAL_MANDATORY', ['NY-SOP-07', 'NY-SOP-13', 'NY-LIC-09'], {
    rows: ['garage-door-reverse', 'garage-separation', 'garage-floor', 'garage-structure', 'garage-appliances', 'safe-garage-door'],
    triggers: [{ kind: 'areaKind', areaKind: 'garage' }, { kind: 'rowRecorded' }],
    en: ['garage', 'carport', 'garage door', 'garage door opener', 'overhead door', 'photo eye'],
    zh: ['车库', '车棚', '车库门', '开门机', '光电眼']
  }),
  // The inspector's own area row. Inspector-initiated work is never gated (AI policy §3).
  subject('inspector-defined-area', 'NOT_REQUIRED_BY_NY_SOP', ['NY-AGR-07', 'PP-19'], {
    rows: ['other-condition'],
    en: [],
    zh: []
  })
]);

const BY_ID: ReadonlyMap<ScopeSubjectId, ScopeSubject> = new Map(NY_SCOPE_SUBJECTS.map((s) => [s.id, s] as const));

const BY_ROW: ReadonlyMap<string, ScopeSubjectId> = (() => {
  const map = new Map<string, ScopeSubjectId>();
  for (const s of NY_SCOPE_SUBJECTS) for (const row of s.rows) map.set(row, s.id);
  return map;
})();

export function scopeSubject(id: ScopeSubjectId): ScopeSubject {
  const found = BY_ID.get(id);
  if (!found) throw new Error(`unknown scope subject ${id}`);
  return found;
}

export function isScopeSubjectId(value: unknown): value is ScopeSubjectId {
  return typeof value === 'string' && BY_ID.has(value as ScopeSubjectId);
}

// The subject of a checklist template row, or null for an inspector-added row (which the gate
// treats as the inspector's own work, never as an AI-raised subject).
export function subjectOfTemplateRow(templateId: string | null): ScopeSubjectId | null {
  return templateId === null ? null : (BY_ROW.get(templateId) ?? null);
}

// Every subject that carries an obstruction record, in catalogue order.
export const OBSTRUCTION_SUBJECTS: readonly ScopeSubject[] = Object.freeze(NY_SCOPE_SUBJECTS.filter((s) => s.obstruction !== null));

// ---------- the four wording tables the gate reads (deterministic, versioned with this authority) ----------

// CONCEALMENT — what turns "an object is there" into "an in-scope component was not observed"
// (NY-INSPECTION-LIMITATIONS.md §4). Without one of these, an obstruction object is not a
// limitation: it is just something the inspector mentioned.
export const CONCEALMENT_PATTERNS: readonly RegExp[] = Object.freeze([
  /\bnot\s+(?:visible|observed|inspected|accessible)\b/i,
  /\bcould\s+not\s+be\s+(?:seen|observed|inspected|viewed|reached|accessed)\b/i,
  /\bunable\s+to\s+(?:see|observe|inspect|view|access)\b/i,
  /\b(?:concealed|obstructed|blocked|hidden|inaccessible)\b/i,
  /\bcovered\s+(?:by|with|in)\b/i,
  /\bcovers?\s+(?:the|part|parts|most|portions?|a\s+portion)\b/i,
  /\bprevented\s+(?:observation|inspection|access|a\s+view)\b/i,
  /\blimited\s+(?:the\s+)?(?:view|observation|access)\b/i,
  /\bno\s+(?:view|access)\s+(?:of|to)\b/i,
  /遮挡|挡住|被挡|覆盖|看不到|无法看到|未能观察|无法观察|未观察到|不可见|无法接近|妨碍观察|限制了观察/
]);

// A VISIBLE HAZARD ON AN IN-SCOPE SYSTEM (AI policy §3 step 3 case (b)) — an open junction box at
// the pool equipment is an electrical observation, and stays.
export const HAZARD_PATTERNS: readonly RegExp[] = Object.freeze([
  /\bexposed\s+(?:wir\w*|conductors?|splices?|connections?|terminals?|live\s+parts?)\b/i,
  /\bopen\s+(?:junction\s+box|splice|knockout)\b/i,
  /\bmissing\s+(?:cover|cover\s+plate|box\s+cover|guard)\b/i,
  /\buncovered\s+(?:box|splice|wiring|conductor)\b/i,
  /\bdamaged\s+(?:cord|cable|conductor|wiring|insulation)\b/i,
  /\b(?:arcing|scorch\w*|burn\s+marks?|burnt|charred)\b/i,
  /\b(?:shock|fire|safety|trip|fall)\s+hazard\b/i,
  /\belectrocution\b/i,
  /\bscald\w*\b/i,
  /\bgas\s+(?:odor|odour|smell)\b/i,
  /裸露|外露(?:接头|导线|电线)|缺(?:盖板|盖)|烧焦|打火|触电|电击|起火|火灾隐患|安全隐患|烫伤|燃气味|煤气味/
]);

// DETERIORATION THAT THREATENS THE RESIDENTIAL BUILDING (case (c), C2 / F-09) — a leaking array
// penetration is a roof finding even though the array itself is not inspected.
export const BUILDING_THREAT_PATTERNS: readonly RegExp[] = Object.freeze([
  /\bleak(?:s|ing|ed|age)?\b/i,
  /\bwater\s+(?:intrusion|entry|penetration|damage|staining|stains?)\b/i,
  /\bmoisture\s+(?:intrusion|damage)\b/i,
  /\brust(?:ed)?\s*-?\s*through\b/i,
  /\bcorroded\s+through\b/i,
  /\b(?:rot|rotted|rotting|rotten|decayed|decaying)\b/i,
  /\bpenetration\s+(?:at|through|into)\b/i,
  /\bflashing\b/i,
  /\bstaining\s+(?:below|beneath|under)\b/i,
  /\bdamage\s+to\s+the\s+(?:roof|structure|framing|wall|ceiling|foundation)\b/i,
  /渗漏|漏水|渗水|进水|水渍|水损|锈穿|腐朽|腐烂|泛水|屋面损坏|结构损坏/
]);

// A QUESTION ABOUT PRESENCE OR SCOPE — allowed even for a subject this inspection does not cover;
// a question that presupposes evaluating it is not (AI policy §3 step 3, needsClarification row).
export const PRESENCE_QUESTION_PATTERNS: readonly RegExp[] = Object.freeze([
  /\b(?:is|are)\s+there\b/i,
  /\bis\s+(?:a|an|the)\s+[\w\s-]{1,40}\s+(?:present|installed|in\s+place)\b/i,
  /\b(?:present|installed)\s*\?/i,
  /\bincluded\s+in\s+(?:this|the)\s+(?:inspection|scope)\b/i,
  /\bin\s+scope\b/i,
  /\bdoes\s+(?:the|this)\s+(?:house|home|property|building)\s+have\b/i,
  /\bwhat\s+(?:type|kind)\s+of\s+(?:water\s+supply|waste\s+disposal|sewage|septic)\b/i,
  /是否有|有没有|是否存在|是否安装|是否包含|是否属于|是否在(?:本次)?(?:检查|验屋)?范围|供水(?:方式|来源)|排污(?:方式|来源)/
]);

// Subjects that New York does not require this product to inspect, in catalogue order.
export const NOT_REQUIRED_SUBJECTS: readonly ScopeSubject[] = Object.freeze(
  NY_SCOPE_SUBJECTS.filter((s) => s.legalStatus === 'NOT_REQUIRED_BY_NY_SOP' || s.legalStatus === 'STATUTORY_EXCLUSION')
);
